// Imports for external libraries go here.
import React, { FC, useEffect, useRef, useState } from 'react';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { Link } from '@marriott/mi-ui-library';
import clsx from 'clsx';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { HubHeroBasicLink, HubHeroBasicModel, HubHeroBasicProps } from './HubHeroBasic.types';
import { StyledHubHeroBasic } from './HubHeroBasic.styles';
import { useDCADynamicModelAlloy } from '../../modules/helpers';
import Glide from '@glidejs/glide';

export const HubHeroBasic: FC<HubHeroBasicProps> = (props: HubHeroBasicProps) => {
  /**
   * Fetching the target response
   */
  const mboxParam = props?.model?.mboxParameter;

  /***
   * Makes the call to target if mboxParam is authored
   */
  const targetResponse = useDCADynamicModelAlloy(mboxParam);
  const targetData = targetResponse?.targetData;

  /**
   * Replacing the AEM model data with target data if target Response is present
   */
  const componentValues: HubHeroBasicModel = targetData ? targetData : props?.model;

  /**
   * cardsPerView is 0 on intial render to avoid the flickering on page load....
   */
  const [cardsPerView, setCardsPerView] = useState(0);

  const [activeTab, setActiveTab] = useState(0);

  const componentId = componentValues?.componentId;

  /**
   * Hide's the arrow's by default and when the cardsPerView is less than the hubHeroBasicLinks length
   * post the calcuation's set's isSlideArrowsVisible flag to true....
   */
  const isSlideArrowsVisible = cardsPerView !== 0 ? componentValues?.hubHeroBasicLinks?.length > cardsPerView : false;

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const carouselRootSelector = `#${componentId}`;
    let glide: Glide.Properties | Glide.Static;
    const leftArrow = document?.querySelector(`#${componentId}_left_arrow`);
    const rightArrow = document?.querySelector(`#${componentId}_right_arrow`);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const preventDefault = (e: any) => e.preventDefault();
    if (document?.querySelector(carouselRootSelector)) {
      try {
        glide = new Glide(carouselRootSelector, {
          type: 'slider',
          perView: cardsPerView,
          startAt: 0,
          rewind: false,
          bound: true,
          gap: 1,
        }).mount({ ArrowDisabler });

        leftArrow?.addEventListener('touchstart', preventDefault);
        rightArrow?.addEventListener('touchstart', preventDefault);
      } catch (error) {
        // log.error(`Error in initializing Glide for ${componentId}`, error);
      }
    }
    return () => {
      if (glide) {
        (glide as Glide.Properties)?.destroy();
      }
      leftArrow?.removeEventListener('touchstart', preventDefault);
      rightArrow?.removeEventListener('touchstart', preventDefault);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentId, cardsPerView]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ArrowDisabler = function (Glide: any, Components: any) {
    return {
      mount() {
        // Only in effect when rewinding is disabled
        if (Glide.settings.rewind) {
          return;
        }
        const left = document?.querySelector('.glide__arrow--left');

        left?.classList.remove('is-disabled');
        Glide.on(['mount.after', 'run'], () => {
          // Filter out arrows_control
          for (const controlItem of Components.Controls.items) {
            if (controlItem.className !== 'glide__arrows') {
              continue;
            }

            // Set left arrow state
            const left = controlItem?.querySelector('.glide__arrow--left');
            if (left) {
              if (Glide.index === 0) {
                left.classList.add('is-disabled'); // Disable on first slide
              } else {
                left.classList.remove('is-disabled'); // Enable on other slides
              }
            }

            // Set right arrow state
            const right = controlItem?.querySelector('.glide__arrow--right');
            if (right) {
              if (Glide.index === Components.Sizes?.length - Glide.settings.perView) {
                right.classList.add('is-disabled'); // Disable on last slide
              } else {
                right.classList.remove('is-disabled'); // Enable on other slides
              }
            }
          }
        });
      },
    };
  };

  const handleTabClick = (tabidx: number) => {
    setActiveTab(tabidx);
  };

  const getTabWithLongestName = (hubHeroBasicLinks: HubHeroBasicLink[]): HubHeroBasicLink | undefined => {
    if (!hubHeroBasicLinks?.length) return undefined;

    const longestTab = hubHeroBasicLinks.reduce((longest: HubHeroBasicLink, current: HubHeroBasicLink | undefined) => {
      if (current) {
        const currentLength = current.ctaProperties.linkText?.length || 0;
        return currentLength > (longest.ctaProperties.linkText?.length || 0) ||
          (currentLength === (longest.ctaProperties.linkText?.length || 0) &&
            (current?.ctaProperties.linkText || '') > (longest.ctaProperties.linkText || ''))
          ? current
          : longest;
      }
      return longest;
    });

    return longestTab;
  };

  const handleResize = () => {
    const divWidth = containerRef?.current?.offsetWidth || 0;
    const longestTab = getTabWithLongestName(componentValues?.hubHeroBasicLinks);
    const longestTabWidth = (longestTab?.ctaProperties.linkText?.length || 0) * 10;
    const tabsInAView = Math.floor(divWidth / (longestTabWidth > 200 ? longestTabWidth : 80));
    const isDesktopView = window?.matchMedia('(min-width: 992px)');

    const tabsPerViewInDesktop = 4;
    /**
     * Fixing the Tabs Per View in Desktop when flexibleTabWidth is true and tabsPerViewInDesktop equals to the tabCounts
     * if not takes the Default Tabs per view
     */
    isDesktopView?.matches && tabsPerViewInDesktop === componentValues?.hubHeroBasicLinks?.length
      ? setCardsPerView(tabsPerViewInDesktop)
      : setCardsPerView(tabsInAView);
  };

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const getLinkElements = (hubHeroBasicLinks: HubHeroBasicLink[], enableTracking: boolean, isAnchorLinks: boolean) => {
    return hubHeroBasicLinks.map((link: HubHeroBasicLink, index: number) => {
      return (
        <div key={index} className="hub-hero-basic-link">
          <Link
            text={link?.ctaProperties.linkText || ''}
            linkClassName={clsx('m-button-s hub-hero-basic-cta-link p-0', enableTracking ? 'custom_click_track' : '', {
              active: activeTab === index,
            })}
            linkHref={`${isAnchorLinks ? '#' : ''}${link?.ctaProperties.linkUrl || ''}`}
            target={link?.ctaProperties.openInANewTab ? '_blank' : '_self'}
            linkType={link?.ctaProperties?.linkType}
            custom_click_track_value={enableTracking ? link?.clickTrackValue || '' : ''}
            callback={() => handleTabClick(index)}
          />
        </div>
      );
    });
  };

  return (
    <StyledHubHeroBasic>
      <div className={clsx('container hub-hero-basic px-0', isSlideArrowsVisible ? 'pt-3' : '')}>
        <div className="hub-hero-basic-links-wrapper px-3 px-md-0">
          <div id={`${componentId}`} className={clsx(`glide col-12 px-0`)}>
            <div className={clsx('glide__track')} data-glide-el="track" ref={containerRef}>
              <ul
                className={clsx(
                  `hub-hero-basic-links`,
                  'glide__slides',
                  !isSlideArrowsVisible ? 'tabs-only no-slider' : ''
                )}
              >
                {componentValues?.hubHeroBasicLinks &&
                  getLinkElements(
                    componentValues?.hubHeroBasicLinks,
                    componentValues?.enableTracking,
                    componentValues?.isAnchorLinks
                  )}
              </ul>
            </div>
            {isSlideArrowsVisible && (
              <div className="center-align">
                <div className="carouselControlType1 tab-controls">
                  <div className="glide__arrows" data-glide-el="controls">
                    <button
                      className={clsx('left-arrow', 'glide__arrow--left', 'alt-arrow')}
                      id={`${componentId}_left_arrow`}
                      data-glide-dir="<"
                      aria-label="left-arrow"
                      data-custom_click_track_value={`internal`}
                    >
                      <span className="icon-arrow-left" id={`${componentId}_left_arrow`} data-id="left"></span>
                    </button>
                  </div>
                  <div className="glide__arrows" data-glide-el="controls">
                    <button
                      className={clsx('right-arrow', 'glide__arrow--right', 'alt-arrow')}
                      id={`${componentId}_right_arrow`}
                      data-glide-dir=">"
                      aria-label="right-arrow"
                      data-custom_click_track_value={`internal`}
                    >
                      <span className="icon-arrow-right" id={`${componentId}_right_arrow`} data-id="right"></span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </StyledHubHeroBasic>
  );
};

export const HubHeroBasicConfig = {
  emptyLabel: 'LuxuryHubHeroBasic',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/hub-hero-basic/v1/hub-hero-basic`,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const HubHeroBasicEditable = (props: any) => (
  <div data-testid="luxury-HubHeroBasic" data-component-name="m-luxury-HubHeroBasic">
    <EditableComponent config={HubHeroBasicConfig} {...props}>
      <HubHeroBasic {...props} />
    </EditableComponent>
  </div>
);
